"use client"

import { getAnalyticsMode } from "./analytics-mode"
import { serviceGroup } from "./service-analytics"

declare global {
  interface Window {
    /**
     * [GtagInitScript] が定義し gtag.js と共有する dataLayer 経由の gtag。
     * dataLayer 自体はここでは宣言しない（型が食い違うと宣言のマージに失敗する）。
     */
    gtag?: (...args: unknown[]) => void
  }
}

/**
 * GA4 カスタムイベント送信のラッパー。
 *
 * 計測イベント一覧（GA4 探索レポートで分析する想定）:
 * - `service_view` { service_id, page_path } 事業ページの閲覧
 * - `work_view` { service_id, cta_location } 作品写真の拡大
 * - service_idを持つイベントにservice_groupを共通付与。社内確認・非本番は送信しない。
 * - `guide_service_click` { slug, service_id, destination_path, cta_location } 記事からサービスへの遷移
 * - `contact_cta_click` { page_path, form_type, service_id, cta_location } 既存の全事業フォーム入口
 * - `cta_click`      { cta_location, service_id, destination } 全事業の相談導線のクリック
 *                    （BrickInquiryLink）。導線を開いた数で、送信完了ではない。
 * - `generate_lead` { form_type, service_count } 自作フォームの受付成功
 * - `material_download` { service_id } 完了画面の資料リンクのクリック
 * - `ask_ai_click`   { slug, target, mode, prompt_len?, profile_filled, overflow? }
 *                    ガイド末尾 ASK AI から各AIサービスへの遷移。target 別・
 *                    mode（prefill / autosubmit）別の選好を見る
 * - `ask_ai_copy`    { slug, source, prompt_len, profile_filled } プロンプトのコピー
 * - `ask_ai_profile` { field, profile_filled } 読者プロファイル（業種/規模/目的）の選択。
 *                    選択“値”は送らない。自由入力も一切送らない（PII混入防止）
 * - `ai_referral`    { ai_source, landing_path } AI回答サービスからの参照流入。
 *                    完全なreferrer URLは送らず、サービス名だけを匿名で記録する。
 *
 * フォームの自由入力・メール・名前・会社名・資料URLはイベントに含めない。
 *
 * gtag() は [GtagInitScript] が HTML パース時に定義する dataLayer 用の
 * キューイング関数。gtag.js の読み込み前に積んだイベントも後から処理される。
 * GA 未設定環境（ローカル等）では gtag が未定義なので何も起きない。
 */
export function trackEvent(
  name: string,
  params: Record<string, string | number | undefined> = {},
) {
  if (getAnalyticsMode() !== "external") return
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  )
  try {
    window.gtag?.("event", name, {
      ...cleaned,
      ...(params.service_id
        ? { service_group: serviceGroup(params.service_id) }
        : {}),
    })
  } catch {
    // 計測はベストエフォート。失敗してもUI動作を妨げない
  }
}
